// playCrew.ts — Run the Play day-pass crews (Jul 12, 2026)
//
// RM 1 adds the RMs he's riding with today; everyone in the crew sees each
// other's PURSUABLE-STAGE leads in Run the Play until ET midnight. Automatic
// add with a heads-up text + in-portal notification and a one-tap Leave.
// Server side: play_crews / play_crew_members tables + play_crewmate_ids()
// + the deals_rm_crew_read RLS policy (migration run_the_play_day_crews).
//
// Verification model (Greg, Jul 12 2026): no hard proof of involvement —
// instead the add is loud (text to the added rep), reversible (instant leave
// drops access), visible (ASM/TL see active crews in the coaching panel),
// and short-lived (dies at midnight). GPS-proximity check deliberately
// deferred to a v2 if the honor system gets abused.

import { supabase } from "./supabase";

const PORTAL_BASE_URL = "https://app.bardinroofing.com";

export interface CrewMemberRow {
  member_id: string;
  added_by: string;
  added_at: string;
  left_at: string | null;
}

export interface CrewJoined {
  id: string;
  crew_date: string;
  created_by: string;
  created_at: string;
  members: CrewMemberRow[];
}

/** Today's date in ET as YYYY-MM-DD (matches the server's crew_date default). */
export function todayET(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
  }).format(new Date());
}

export function activeMembers(c: CrewJoined): CrewMemberRow[] {
  return c.members.filter((m) => !m.left_at);
}

/** All of today's crews with their members (RLS: any authenticated staff). */
export async function fetchTodayCrews(): Promise<CrewJoined[]> {
  const { data, error } = await supabase
    .from("play_crews")
    .select(
      "id,crew_date,created_by,created_at,play_crew_members(member_id,added_by,added_at,left_at)",
    )
    .eq("crew_date", todayET());
  if (error) throw error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map((c) => ({
    id: c.id,
    crew_date: c.crew_date,
    created_by: c.created_by,
    created_at: c.created_at,
    members: c.play_crew_members ?? [],
  }));
}

/**
 * Create my crew for today (creator is also inserted as a member so the
 * visibility is mutual — my crewmates see MY leads too). Returns crew id.
 */
export async function createMyCrew(myId: string): Promise<string> {
  const { data, error } = await supabase
    .from("play_crews")
    .insert({ created_by: myId })
    .select("id")
    .single();
  if (error) throw error;
  const crewId = (data as { id: string }).id;
  const { error: selfErr } = await supabase.from("play_crew_members").insert({
    crew_id: crewId,
    member_id: myId,
    added_by: myId,
  });
  if (selfErr) throw selfErr;
  return crewId;
}

/** Add (or re-add after a leave) a member. Upsert resets left_at to null. */
export async function addCrewMember(
  crewId: string,
  memberId: string,
  addedBy: string,
): Promise<void> {
  const { error } = await supabase.from("play_crew_members").upsert(
    {
      crew_id: crewId,
      member_id: memberId,
      added_by: addedBy,
      added_at: new Date().toISOString(),
      left_at: null,
    },
    { onConflict: "crew_id,member_id" },
  );
  if (error) throw error;
}

/** Leave / remove — access to each other's leads drops immediately. */
export async function leaveCrew(crewId: string, memberId: string): Promise<void> {
  const { error } = await supabase
    .from("play_crew_members")
    .update({ left_at: new Date().toISOString() })
    .eq("crew_id", crewId)
    .eq("member_id", memberId);
  if (error) throw error;
}

/**
 * Heads-up to the rep who was just crewed up: in-portal notification always,
 * Team-line SMS when they have a phone on file (staff-send-sms resolves the
 * sender from the caller's JWT). Fire-and-forget — never blocks the add.
 */
export async function notifyCrewAdded(
  addedId: string,
  adder: { id: string; full_name: string },
): Promise<void> {
  try {
    const { data: added, error: lookupErr } = await supabase
      .from("team_roster")
      .select("id,full_name,phone")
      .eq("id", addedId)
      .eq("is_active", true)
      .maybeSingle();
    if (lookupErr || !added) {
      if (lookupErr) console.warn("[playCrew] roster lookup failed:", lookupErr.message);
      return;
    }

    const url = `${PORTAL_BASE_URL}/run-the-play`;
    const inAppPromise = supabase.from("notifications").insert({
      user_id: added.id,
      type: "play_crew_added",
      title: `Crewed up — ${adder.full_name}`,
      body: `${adder.full_name} added you to their Run the Play crew for today. You can see and route each other's pursuable leads until midnight. Not riding together? Leave the crew from Run the Play.`,
      metadata: { url: "/run-the-play", adder_id: adder.id },
    });

    const phoneRaw = (added as { phone?: string | null }).phone;
    const digits = (phoneRaw ?? "").replace(/\D/g, "");
    const phoneE164 =
      digits.length === 10
        ? `+1${digits}`
        : digits.length === 11 && digits.startsWith("1")
          ? `+${digits}`
          : null;

    let smsPromise: Promise<unknown> = Promise.resolve();
    if (phoneE164) {
      smsPromise = supabase.functions.invoke("staff-send-sms", {
        body: {
          to: phoneE164,
          body: `Bardin CRM: ${adder.full_name} added you to their Run the Play crew for today — you'll see each other's pursuable leads until midnight. Not riding together? Leave the crew: ${url}`,
          tag: "play_crew_added",
        },
      });
    }

    const [inAppRes, smsRes] = await Promise.allSettled([inAppPromise, smsPromise]);
    if (inAppRes.status === "rejected")
      console.warn("[playCrew] notification insert failed:", inAppRes.reason);
    if (smsRes.status === "rejected")
      console.warn("[playCrew] SMS send failed:", smsRes.reason);
  } catch (err) {
    console.warn("[playCrew] notifyCrewAdded unexpected error:", err);
  }
}
